import { useEffect } from "react";
import { ACADEMY_ENABLED, INVESTOR_PROFILE_ENABLED, CALCULATORS_ENABLED } from "../constants/features";
import { UI, FONT} from "../constants/tokens";
import logo256 from "../assets/suricata-logo-256.png";
import Icon from "./Icon";

const LINKS = [
  { id:"home",        label:"דף הבית",    labelEn:"Home",        icon:"home" },
  { id:"portfolio",   label:"תיק",  labelEn:"Portfolio",   icon:"chart" },
  { id:"exposure",    label:"חשיפות התיק", labelEn:"Portfolio exposures", icon:"globe" },
  { id:"whatif",      label:"מה אם",       labelEn:"What if",     icon:"scale" },
  { id:"profile",     label:"הפרופיל שלי",labelEn:"My Profile",  icon:"person" },
  { id:"simulator",   label:"חשבון תרגול",   labelEn:"Practice account",   icon:"target" },
  { id:"news",        label:"חדשות",       labelEn:"News",        icon:"news" },
  { id:"calendar",    label:"יומן",        labelEn:"Calendar",    icon:"calendar" },
  { id:"ai",          label:"מודיעין שוק",   labelEn:"AI Analysis", icon:"ai" },
  { id:"academy",     label:"אקדמיה",     labelEn:"Academy",     icon:"academy" },
  { id:"calculators", label:"מחשבונים",   labelEn:"Calculators", icon:"calculator" },
  { id:"qa",          label:"שאלות נפוצות",labelEn:"Q&A",         icon:"question" },
  { id:"settings",    label:"הגדרות",     labelEn:"Settings",    icon:"settings" },
];

const CURRENCIES = ["USD", "ILS", "EUR"];

export default function MobileSidebar({
  open, onClose,
  tab, onTabClick,
  lang, cur, onCurrencyChange,
  onSignOut, isAdmin = false, onLang,
}) {
  const isRTL = lang !== "en";
  // Hidden screens (constants/features.js): the Academy, the investor profile
  // and the calculators.
  const hidden = new Set([
    ...(ACADEMY_ENABLED ? [] : ["academy"]),
    ...(INVESTOR_PROFILE_ENABLED ? [] : ["profile"]),
    ...(CALCULATORS_ENABLED ? [] : ["calculators"]),
  ]);
  const shown = LINKS.filter(l => !hidden.has(l.id));
  const links = isAdmin ? [...shown, { id: "admin", label: "ניהול", labelEn: "Admin", icon: "shield" }] : shown;

  // Lock body scroll while open
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!open) return null;

  const slide = isRTL
    ? { right:0, transform: open ? "translateX(0)" : "translateX(100%)" }
    : { left:0,  transform: open ? "translateX(0)" : "translateX(-100%)" };

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{ position:"fixed", inset:0, background:UI.scrim, zIndex:299,
                 animation:"sidebarFadeIn .2s ease" }}
      />

      {/* Drawer */}
      <div
        dir={isRTL ? "rtl" : "ltr"}
        style={{ position:"fixed", top:0, bottom:0, width:280, maxWidth:"85vw",
                 background:UI.surface.raised, borderLeft: isRTL ? "none" : `1px solid ${UI.line.hairline}`,
                 borderRight: isRTL ? `1px solid ${UI.line.hairline}` : "none",
                 zIndex:300, display:"flex", flexDirection:"column",
                 animation:"sidebarSlideIn .22s cubic-bezier(.32,.72,0,1)",
                 ...slide }}
      >
        {/* Header */}
        <div style={{ padding:"14px 16px", borderBottom:`1px solid ${UI.line.hairline}`,
                      display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div
            onClick={() => { onTabClick("home"); onClose(); }}
            style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer" }}
          >
            <img src={logo256} alt="Suricata" style={{ width:34, height:34, borderRadius:"22%", background:UI.logoPlate, objectFit:"cover", display:"block" }}/>
            <span dir="ltr" style={{ fontWeight:700, fontSize:16, color:UI.text.primary, fontFamily:FONT.latin, unicodeBidi:"isolate" }}>Suricata</span>
          </div>
          <button
            onClick={onClose}
            style={{ background:"transparent", border:`1px solid ${UI.line.hairline}`, borderRadius:6,
                     width:32, height:32, display:"flex", alignItems:"center", justifyContent:"center",
                     cursor:"pointer", color:UI.text.secondary }}>
            <Icon name="x" size={16} color={UI.text.secondary}/>
          </button>
        </div>

        {/* Nav links */}
        <div style={{ flex:1, overflowY:"auto", padding:"8px 8px" }}>
          {links.map(link => {
            const active = tab === link.id;
            return (
              <button
                key={link.id}
                onClick={() => { onTabClick(link.id); onClose(); }}
                style={{ width:"100%", background: active ? UI.state.surface : "transparent",
                         border:"none", borderRight: isRTL ? (active ? `2px solid ${UI.line.strong}` : "2px solid transparent") : "none",
                         borderLeft: !isRTL ? (active ? `2px solid ${UI.line.strong}` : "2px solid transparent") : "none",
                         borderRadius:6, padding:"12px 14px", display:"flex", alignItems:"center",
                         gap:12, cursor:"pointer", textAlign: isRTL ? "right" : "left" }}
              >
                <Icon name={link.icon} size={19} color={active ? UI.state.fg : UI.text.secondary}/>
                <span style={{ fontSize:15, fontWeight: active ? 700 : 400,
                               color: active ? UI.state.fg : UI.text.secondary }}>
                  {lang === "en" ? link.labelEn : link.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Footer: currency + sign out */}
        <div style={{ padding:"12px 12px", borderTop:`1px solid ${UI.line.hairline}`,
                      display:"flex", flexDirection:"column", gap:8 }}>
          {/* Currency selector */}
          <div style={{ display:"flex", gap:4 }}>
            {CURRENCIES.map(c => (
              <button key={c} onClick={() => onCurrencyChange(c)}
                style={{ flex:1, background: cur===c ? UI.fill.subtle : "transparent",
                         border:`1px solid ${cur===c ? UI.line.strong : UI.line.hairline}`,
                         borderRadius:6, padding:"6px 0", fontSize:13, fontWeight:700,
                         color: cur===c ? UI.text.primary : UI.text.secondary, cursor:"pointer" }}>
                {c}
              </button>
            ))}
          </div>
          {/* Language toggle (moved off the mobile header) */}
          {onLang && (
            <div style={{ display:"flex", gap:4 }}>
              {[{ code:"he", label:"🇮🇱 עברית" }, { code:"en", label:"🇺🇸 English" }].map(({ code, label }) => (
                <button key={code} onClick={() => onLang(code)}
                  style={{ flex:1, background: lang===code ? UI.fill.subtle : "transparent",
                           border:`1px solid ${lang===code ? UI.line.strong : UI.line.hairline}`,
                           borderRadius:6, padding:"8px 0", fontSize:12, fontWeight:700,
                           color: lang===code ? UI.text.primary : UI.text.secondary, cursor:"pointer" }}>
                  {label}
                </button>
              ))}
            </div>
          )}
          {/* Sign out */}
          <button
            onClick={onSignOut}
            style={{ width:"100%", background:"transparent", border:`1px solid ${UI.line.hairline}`,
                     borderRadius:6, padding:"10px", fontSize:14, fontWeight:700,
                     color:UI.text.secondary, cursor:"pointer", display:"flex", alignItems:"center",
                     justifyContent:"center", gap:8 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" stroke={UI.text.secondary} strokeWidth="1.5" strokeLinecap="round"/>
              <polyline points="16,17 21,12 16,7" stroke={UI.text.secondary} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="21" y1="12" x2="9" y2="12" stroke={UI.text.secondary} strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            {lang === "en" ? "Sign Out" : "יציאה"}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes sidebarFadeIn {
          from { opacity:0; }
          to   { opacity:1; }
        }
        @keyframes sidebarSlideIn {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
      `}</style>
    </>
  );
}
